import {
	Edit05,
	ImagePlus,
	Trash01,
	Upload01,
	User01,
	ZoomIn,
	ZoomOut,
} from "@untitledui/icons";
import { useCallback, useId, useRef, useState } from "react";
import {
	DialogTrigger as AriaDialogTrigger,
	Heading as AriaHeading,
} from "react-aria-components";
import type { Area, Point } from "react-easy-crop";
import Cropper from "react-easy-crop";
import { Avatar } from "@/components/Atoms/Avatar/Avatar";
import {
	Dialog,
	Modal,
	ModalOverlay,
} from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";

export interface EditAvatarModalProps {
	/** Whether the modal is open */
	isOpen: boolean;
	/** Callback to change open state */
	setIsOpen: (open: boolean) => void;
	/** Current avatar URL (if exists) */
	currentAvatarSrc?: string | null;
	/** Callback when save button is clicked with the cropped image blob */
	onSave: (croppedImage: Blob) => void | Promise<void>;
	/** Callback when remove button is clicked */
	onRemove?: () => void | Promise<void>;
	/** Whether the save action is in progress */
	isSaving?: boolean;
	/** Whether the remove action is in progress */
	isRemoving?: boolean;
}

/**
 * Crop the image based on the crop area and return a blob
 */
async function getCroppedImg(
	imageSrc: string,
	pixelCrop: Area,
): Promise<Blob | null> {
	const image = await createImage(imageSrc);
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");

	if (!ctx) {
		return null;
	}

	// Set canvas dimensions to the crop size
	canvas.width = pixelCrop.width;
	canvas.height = pixelCrop.height;

	// Draw the cropped image
	ctx.drawImage(
		image,
		pixelCrop.x,
		pixelCrop.y,
		pixelCrop.width,
		pixelCrop.height,
		0,
		0,
		pixelCrop.width,
		pixelCrop.height,
	);

	// Return as blob
	return new Promise((resolve) => {
		canvas.toBlob(
			(blob) => {
				resolve(blob);
			},
			"image/jpeg",
			0.9,
		);
	});
}

/**
 * Create an HTMLImageElement from a source URL
 */
function createImage(url: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.addEventListener("load", () => resolve(image));
		image.addEventListener("error", (error) => reject(error));
		image.src = url;
	});
}

export const EditAvatarModal = ({
	isOpen,
	setIsOpen,
	currentAvatarSrc,
	onSave,
	onRemove,
	isSaving = false,
	isRemoving = false,
}: EditAvatarModalProps) => {
	const { t } = useAppTranslation();
	const titleId = useId();
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Image state
	const [selectedImage, setSelectedImage] = useState<string | null>(null);
	const [fileError, setFileError] = useState<string | null>(null);

	// Crop state
	const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(0.8);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

	// Zoom constraints
	const MIN_ZOOM = 0.5;
	const MAX_ZOOM = 3;

	const isLoading = isSaving || isRemoving;

	// Max file size: 5MB
	const MAX_FILE_SIZE = 5 * 1024 * 1024;

	// Handle file selection
	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		setFileError(null);

		if (file) {
			// Validate file size
			if (file.size > MAX_FILE_SIZE) {
				setFileError(t("userProfile.avatar.tooLarge"));
				event.target.value = "";
				return;
			}

			const reader = new FileReader();
			reader.onload = () => {
				setSelectedImage(reader.result as string);
				// Reset crop/zoom when new image is selected
				setCrop({ x: 0, y: 0 });
				setZoom(0.8);
			};
			reader.readAsDataURL(file);
		}
		// Reset input so same file can be selected again
		event.target.value = "";
	};

	// Handle crop complete
	const onCropComplete = useCallback(
		(_croppedArea: Area, croppedAreaPixels: Area) => {
			setCroppedAreaPixels(croppedAreaPixels);
		},
		[],
	);

	// Handle save
	const handleSave = async () => {
		if (!selectedImage || !croppedAreaPixels) return;

		const croppedImage = await getCroppedImg(selectedImage, croppedAreaPixels);
		if (croppedImage) {
			await onSave(croppedImage);
			handleClose();
		}
	};

	// Handle remove
	const handleRemove = async () => {
		if (onRemove) {
			await onRemove();
			handleClose();
		}
	};

	// Handle close/cancel
	const handleClose = () => {
		setSelectedImage(null);
		setFileError(null);
		setCrop({ x: 0, y: 0 });
		setZoom(1);
		setCroppedAreaPixels(null);
		setIsOpen(false);
	};

	// Trigger file input
	const triggerFileInput = () => {
		fileInputRef.current?.click();
	};

	// Load existing avatar into cropper for editing
	// Convert blob URL to data URL to avoid CORS issues with canvas
	const handleEditExisting = async () => {
		if (currentAvatarSrc) {
			try {
				// Fetch the blob from the URL and convert to data URL
				const response = await fetch(currentAvatarSrc);
				const blob = await response.blob();
				const reader = new FileReader();
				reader.onload = () => {
					setSelectedImage(reader.result as string);
					setCrop({ x: 0, y: 0 });
					setZoom(1);
					setFileError(null);
				};
				reader.onerror = () => {
					setFileError(t("userProfile.avatar.modal.loadError"));
				};
				reader.readAsDataURL(blob);
			} catch {
				setFileError(t("userProfile.avatar.modal.loadError"));
			}
		}
	};

	// Check if we have a removable avatar (existing avatar and no new selection)
	const canRemove = !!currentAvatarSrc && !selectedImage && !!onRemove;

	// Check if we can edit existing avatar
	const canEditExisting = !!currentAvatarSrc && !selectedImage;

	return (
		<AriaDialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
			<ModalOverlay isDismissable={!isLoading}>
				<Modal>
					<Dialog>
						<div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-md">
							{/* Header */}
							<div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
								<div className="flex items-center gap-3">
									<div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50">
										<ImagePlus className="h-5 w-5 text-brand-600" />
									</div>
									<div>
										<AriaHeading
											id={titleId}
											slot="title"
											className="text-lg font-semibold text-primary"
										>
											{t("userProfile.avatar.modal.title")}
										</AriaHeading>
										<p className="text-sm text-tertiary">
											{t("userProfile.avatar.modal.description")}
										</p>
									</div>
								</div>
							</div>

							{/* Content */}
							<div className="px-4 py-6 sm:px-6">
								{/* Hidden file input */}
								<input
									ref={fileInputRef}
									type="file"
									accept="image/jpeg,image/png,image/gif,image/webp"
									onChange={handleFileSelect}
									className="hidden"
									aria-label={t("userProfile.avatar.modal.selectFile")}
								/>

								{selectedImage ? (
									/* Cropper view */
									<div className="flex flex-col gap-4">
										<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-gray-100">
											<Cropper
												image={selectedImage}
												crop={crop}
												zoom={zoom}
												minZoom={MIN_ZOOM}
												maxZoom={MAX_ZOOM}
												aspect={1}
												cropShape="round"
												showGrid={false}
												onCropChange={setCrop}
												onCropComplete={onCropComplete}
												onZoomChange={setZoom}
											/>
										</div>

										{/* Zoom controls */}
										<div className="flex items-center gap-3">
											<ZoomOut className="h-4 w-4 text-gray-400" />
											<input
												type="range"
												min={MIN_ZOOM}
												max={MAX_ZOOM}
												step={0.1}
												value={zoom}
												onChange={(e) => setZoom(Number(e.target.value))}
												className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-brand-600"
												aria-label={t("userProfile.avatar.modal.zoom")}
											/>
											<ZoomIn className="h-4 w-4 text-gray-400" />
										</div>

										{/* Change image button */}
										<Button
											color="tertiary"
											size="sm"
											onClick={triggerFileInput}
											className="self-center"
											iconLeading={Upload01}
										>
											{t("userProfile.avatar.modal.changeImage")}
										</Button>
									</div>
								) : (
									/* Upload view */
									<div className="flex flex-col items-center gap-4">
										{/* Current avatar preview */}
										<div className="relative">
											<Avatar
												size="2xl"
												src={currentAvatarSrc}
												placeholderIcon={({ className }) => (
													<User01 className={cx(className, "text-gray-400")} />
												)}
												className="!h-32 !w-32 !bg-gray-100"
											/>
										</div>

										{/* Action buttons for existing avatar */}
										{canEditExisting && (
											<div className="flex gap-3">
												<Button
													color="secondary"
													size="sm"
													onClick={handleEditExisting}
													iconLeading={Edit05}
												>
													{t("userProfile.avatar.modal.editExisting")}
												</Button>
												<Button
													color="secondary"
													size="sm"
													onClick={triggerFileInput}
													iconLeading={Upload01}
												>
													{t("userProfile.avatar.modal.uploadNew")}
												</Button>
											</div>
										)}

										{/* Upload dropzone - only show when no existing avatar */}
										{!currentAvatarSrc && (
											<button
												type="button"
												onClick={triggerFileInput}
												className={cx(
													"flex w-full flex-col items-center gap-3 rounded-xl border-2 border-dashed p-6",
													fileError
														? "border-error-300 bg-error-25"
														: "border-gray-200",
													"transition-colors hover:border-brand-300 hover:bg-brand-25",
													"focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
												)}
											>
												<div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
													<Upload01 className="h-5 w-5 text-gray-600" />
												</div>
												<div className="text-center">
													<p className="text-sm font-semibold text-brand-700">
														{t("userProfile.avatar.modal.uploadPrompt")}
													</p>
													<p className="text-xs text-tertiary">
														{t("userProfile.avatar.modal.uploadHint")}
													</p>
												</div>
											</button>
										)}

										{/* Error message */}
										{fileError && (
											<p className="text-sm text-error-600">{fileError}</p>
										)}
									</div>
								)}
							</div>

							{/* Footer */}
							<div className="border-t border-secondary p-4 sm:p-6">
								<div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
									{/* Left side - Remove button */}
									<div>
										{canRemove && (
											<Button
												color="tertiary-destructive"
												size="md"
												onClick={handleRemove}
												isLoading={isRemoving}
												isDisabled={isLoading}
												iconLeading={Trash01}
											>
												{t("userProfile.avatar.modal.remove")}
											</Button>
										)}
									</div>

									{/* Right side - Cancel/Save buttons */}
									<div className="flex flex-col-reverse gap-3 sm:flex-row">
										<Button
											color="secondary"
											size="md"
											onClick={handleClose}
											isDisabled={isLoading}
										>
											{t("common.actions.cancel")}
										</Button>
										{selectedImage && (
											<Button
												color="primary"
												size="md"
												onClick={handleSave}
												isLoading={isSaving}
												isDisabled={isLoading || !croppedAreaPixels}
											>
												{t("common.actions.save")}
											</Button>
										)}
									</div>
								</div>
							</div>
						</div>
					</Dialog>
				</Modal>
			</ModalOverlay>
		</AriaDialogTrigger>
	);
};
